import React, { useState, useEffect } from "react";
import Widget from "../../components/Widget/Widget.js";
import styles from "./SolarFacilities.module.scss";
import { useSelector } from "react-redux";
import Select from "react-select";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import CustomChart from "./components/MonitoringComponents/CustomChart.js";
import DayCapStackedBarChart from "./components/MonitoringComponents/DayCapStackedBarChart.js";
import moment from "moment";
import {
  Label 
} from "reactstrap";
import { 
  Tabs,
  Tab,
  Button,
  ButtonGroup
} from "@material-ui/core";
import { 
  areDatesOnSameDay,
  convertJsToUnixSeconds,
  convertUnixToJs,
  convertUnixToJsWithISODate,
  getEndOfDay,
  getStartOfDay,
} from "./components/MonitoringComponents/Helper";

function SolarFacilities() {
  // --- Facility Select ---
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [isFacilitiesLoaded, setIsFacilitiesLoaded] = useState(false);

  // --- Data mode switch ---
  const [dataMode, setDataMode] = useState('live');
  const isLiveData = dataMode === 'live';

  // --- Date Picker ---
  const [startPicker, setStartPicker] = useState(getStartOfDay(new Date()));
  const [endPicker, setEndPicker] = useState(new Date());

  // --- Interval Picker ---
  const [intervalPicker, setIntervalPicker] = useState('30 mins');
  const intervals = ['30 mins', '1 hour', '6 hours', '1 day', '7 days', '1 month'];

  // --- Type Picker ---
  const [typePicker, setTypePicker] = useState('Avg');
  const types = ['Avg', 'Max', 'Min'];

  const [currentTime, setCurrentTime] = useState(moment());

  // --- Line Chart Data ---
  const [rawPowerData, setRawPowerData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [dayCapData, setDayCapData] = useState([]);
  const [dataProcessingStatus, setDataProcessingStatus] = useState('loading');
  const [isDateRangeChanging, setIsDateRangeChanging] = useState(false);

  // Fetch facilities on component mount
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await axios.get("/solar_facilities");
        const facilitiesData = response.data.map(facility => ({
          value: facility.id,
          label: facility.name
        }));
        
        setFacilities(facilitiesData);
        
        // Set first facility as default if available
        if (facilitiesData.length > 0) {
          const firstFacility = facilitiesData[0];
          setSelectedFacility(firstFacility);
        } else {
          // Ensure we have a default null value if no facilities
          setSelectedFacility(null);
        }
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([]);
        setSelectedFacility(null);
      }
    };

    fetchFacilities();
  }, []);

  // Get interval in seconds
  const getIntervalInSeconds = (interval) => {
    switch (interval) {
      case "30 mins": return 30 * 60;
      case "1 hour": return 60 * 60;
      case "6 hours": return 6 * 60 * 60;
      case "1 day": return 24 * 60 * 60;
      case "7 days": return 7 * 24 * 60 * 60;
      case "1 month": return 30 * 24 * 60 * 60;
      default: return 30 * 60;
    }
  }

  // Fetch solar facility data
  useEffect(() => {
    const fetchSolarData = async (facilityId, startTimestamp, endTimestamp) => {
      if (!facilityId) return;

      try {
        const response = await axios.get("/solar_facilities/getSolarFacilityData", {
          params: {
            facilityId: facilityId,
            startTime: startTimestamp,
            endTime: endTimestamp,
          }, 
        });

        if (response.data.length === 0) {
          setDataProcessingStatus('no data');
          return false;
        }

        if (response.status === 200) {
          setDataProcessingStatus('loading');
          setRawPowerData(response.data);
          return true;
        }

        setDataProcessingStatus('no data');
        return false;
      } catch (error) {
        console.error("Error fetching solar data:", error);
        setDataProcessingStatus('no data');
        return false;
      }
    }

    const retrieveSolarData = async () => {
      // Set loading state when date range is changing
      if (!isLiveData) {
        setIsDateRangeChanging(true);
      }
      setDataProcessingStatus('loading');

      if (isLiveData) {
        // Live data displays last 6 hours of power data
        const endDate = new Date(); // always use "now"
        const endTimestamp = convertJsToUnixSeconds(endDate);
        const startTimestamp = endTimestamp - 21600; // last 6 hours
        await fetchSolarData(selectedFacility?.value, startTimestamp, endTimestamp);
      } else {
        // Historical displays power data in selected date range
        const today = new Date();
        const endDate = areDatesOnSameDay(endPicker, today) ? today : endPicker;
        const endTimestamp = convertJsToUnixSeconds(endDate);
        const startTimestamp = convertJsToUnixSeconds(startPicker);
        await fetchSolarData(selectedFacility?.value, startTimestamp, endTimestamp);
      }

      setCurrentTime(moment());
      
      // Reset date range changing state after data is loaded
      if (!isLiveData) {
        setIsDateRangeChanging(false);
      }
    };

    if (selectedFacility) {
      retrieveSolarData();
    }

    // Refresh every 300s if isLiveData is true
    let intervalId;
    if (isLiveData) {
      intervalId = setInterval(() => {
        retrieveSolarData();
      }, 300000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedFacility, isLiveData, startPicker, endPicker]);

  // Process data for charting
  useEffect(() => {
    if (!rawPowerData || rawPowerData.length === 0) {
      setLineChartData([]);
      return;
    }

    setDataProcessingStatus('loading');

    // Helper: Aggregate raw power data by timestamp
    const aggregateRawPowerData = (powerData) => {
      const intervalInSeconds = getIntervalInSeconds(intervalPicker);
      const grouped = {};

      powerData.forEach(item => {
        const batch = Number(item.batch);
        // const intervalBatch = batch - (batch % intervalInSeconds);
        const SG_OFFSET = 8 * 3600;
        const intervalBatch = Math.floor((batch + SG_OFFSET) / intervalInSeconds) * intervalInSeconds - SG_OFFSET;

        if (!grouped[intervalBatch]) grouped[intervalBatch] = [];
        
        // For each inverter device, collect its power values
        const deviceData = {};
        if (item.active_power) {
          for (const [key, value] of Object.entries(item.active_power)) {
            deviceData[key] = parseFloat(value);
          }
        }
        grouped[intervalBatch].push(deviceData);
      });

      // Calculate aggregation based on selected type (Avg, Min, Max)
      const resultArray = [];
      for (const [timestamp, values] of Object.entries(grouped)) {
        const aggData = { batch: timestamp };
        
        // For each device, calculate the aggregation based on type
        const devices = Object.keys(values[0] || {});
        devices.forEach(device => {
          const deviceValues = values.map(item => item[device] || 0);
          
          let result;
          switch (typePicker) {
            case 'Min':
              result = Math.min(...deviceValues).toFixed(3);
              break;
            case 'Max':
              result = Math.max(...deviceValues).toFixed(3);
              break;
            case 'Avg':
            default:
              const sum = deviceValues.reduce((total, item) => total + item, 0);
              result = (sum / deviceValues.length).toFixed(3);
              break;
          }
          
          aggData[device] = result;
        });
        
        resultArray.push(aggData);
      }

      return resultArray.sort((a, b) => a.batch - b.batch);
    };

    // Helper: Transform data for charting
    const transformPowerDataForChart = (powerData) => {
      // Return data in format for charting:
      // { "jsTime": converted timestamp, "Inverter(COM2-2)": 24.527, ... }
      let resultArray = powerData
        .filter(obj => obj && typeof obj === 'object' && obj.batch !== undefined) // Filter out invalid objects
        .map((obj) => {
          if (!obj || obj.batch === undefined) return null; // safeguard against undefined entries

          // Create a new object for charting data
          const newObj = {};
          
          // Copy jsTime from batch
          if (obj.batch !== undefined) {
            newObj.jsTime = intervalPicker.includes("day") || intervalPicker.includes("month") ? 
              convertUnixToJsWithISODate(obj.batch) : convertUnixToJs(obj.batch);
          }
          
          // Handle active_power structure - flatten it to make inverter data direct properties
          if (obj.active_power && typeof obj.active_power === 'object') {
            for (const [key, value] of Object.entries(obj.active_power)) {
              newObj[key] = parseFloat(value);
            }
          } else if (obj && typeof obj === 'object') {
            // If no active_power, treat all non-batch properties as inverter data
            for (const [key, value] of Object.entries(obj)) {
              if (key !== 'batch' && key !== 'jsTime') {
                newObj[key] = parseFloat(value);
              }
            }
          }
          
          return newObj;
        })
        .filter(item => item !== null);

      return resultArray;
    };

    // Helper: Process day_cap data for stacked bar chart (always process when we have day_cap data)
    const processDayCapData = (powerData) => {
      // Process day_cap data regardless of interval, as long as we have the data
      // This is now handled in the main function logic
      
      // Group data by date and find max day_cap values for each inverter per day
      const groupedByDate = {};

      powerData.forEach(item => {
        // Create date string from timestamp
        const SG_OFFSET = 8 * 3600;
        const date = new Date((item.batch + SG_OFFSET) * 1000);
        const dateString = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        
        if (!groupedByDate[dateString]) {
          groupedByDate[dateString] = { date: dateString, day_cap: {} };
        }
        
        // For each inverter, keep track of the maximum day_cap value for that day
        if (item.day_cap && typeof item.day_cap === 'object') {
          for (const [inverter, value] of Object.entries(item.day_cap)) {
            const floatValue = parseFloat(value) || 0;
            
            if (!groupedByDate[dateString].day_cap[inverter]) {
              groupedByDate[dateString].day_cap[inverter] = floatValue;
            } else {
              // Keep the maximum value
              groupedByDate[dateString].day_cap[inverter] = Math.max(
                groupedByDate[dateString].day_cap[inverter], 
                floatValue
              );
            }
          }
        }
      });

      // Convert to array and sort by date
      const resultArray = Object.values(groupedByDate).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return 0;
      });

      return resultArray;
    };

    const powerData = isLiveData ? rawPowerData : aggregateRawPowerData(rawPowerData);
    
    // Defensive check to ensure powerData is valid
    if (!powerData || !Array.isArray(powerData)) {
      setLineChartData([]);
      setDataProcessingStatus('no data');
      return;
    }
    
    // Additional safety check - ensure we have at least one valid item
    if (powerData.length === 0) {
      setLineChartData([]);
      setDataProcessingStatus('no data');
      return;
    }
    
    try {
      const resultArray = transformPowerDataForChart(powerData);
      setLineChartData(resultArray);
      
      const processedDayCapData = processDayCapData(rawPowerData);
      setDayCapData(processedDayCapData);
      
      setDataProcessingStatus('loaded data');
    } catch (error) {
      console.error("Error processing chart data:", error);
      setLineChartData([]);
      setDataProcessingStatus('no data');
    }
  }, [intervalPicker, isLiveData, rawPowerData, startPicker, endPicker, typePicker]);

  return (
    <div>
      <div className={styles.MonitoringAssetOptionContainer}>
        <Select
          value={selectedFacility}
          options={facilities}
          onChange={setSelectedFacility}
          classNamePrefix="react-select"
          styles={{
            control: (base) => ({
              ...base,
              backgroundColor: 'var(--input-bg)',
              color: 'var(--body-color)',
              borderColor: 'var(--input-border)'
            }),
            singleValue: (base) => ({ ...base, color: 'var(--body-color)' }),
            menu: (base) => ({ ...base, backgroundColor: 'var(--card-bg)', color: 'var(--body-color)' }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? 'var(--sidebar-hover-bg)' : 'transparent',
              color: 'var(--body-color)'
            })
          }}
          placeholder="Select Facility"
        />
      </div>

      {selectedFacility && (
        <>
          <Tabs
            value={dataMode === 'live' ? 0 : 1}
            onChange={(event, newValue) => {
              if (newValue === 0) {
                setDataMode('live');
              } else {
                setDataMode('historical');
              }
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Live" />
            <Tab label="Historical" />
          </Tabs>

          <div className='m-3'>
            {isLiveData && (
              <div className={styles.InputRow}>
                <div className={styles.HeaderTimeFilter}>
                  <div>
                    Last Updated:{" "}
                    {moment(currentTime).format("DD/MM/YYYY HH:mm:ss")}
                  </div>
                </div>
              </div>
            )}

            {!isLiveData && (
              <div>
                <div className={styles.InputRow}>
                  <label htmlFor="startDate" className="form-label">From</label>
                  <Flatpickr
                    id="startDate"
                    name="startDate"
                    className={styles.DatePicker}
                    options={{
                      enableTime: false,
                      dateFormat: "d-M-Y",
                      minDate: moment().subtract(1, "year").startOf("day").toDate(),
                      maxDate: endPicker,
                    }}
                    value={startPicker}
                    onChange={([newStartDate]) => {
                      setStartPicker(newStartDate);
                      const maxEndDate = moment(newStartDate).add(1, "month")
                                          .endOf("day").toDate();
                      const today = new Date();
                      setEndPicker(maxEndDate > today ? today : maxEndDate);
                    }} 
                  />
                  <label htmlFor="endDate" className="form-label">To</label>
                  <Flatpickr
                    id="endDate"
                    name="endDate"
                    className={styles.DatePicker}
                    options={{
                      enableTime: false,
                      dateFormat: "d-M-Y",
                      minDate: startPicker,
                      maxDate: moment.min(
                        moment(startPicker).add(3, "month").endOf("day"),
                        moment().endOf("day")
                      ).toDate(),
                    }}
                    value={endPicker}
                    onChange={([newEndDate]) => {
                      const today = new Date();
                      if (areDatesOnSameDay(newEndDate, today)) {
                        setEndPicker(today);
                      } else {
                        setEndPicker(getEndOfDay(newEndDate));
                      }
                    }}
                  />
                </div>

                <div className={styles.InputRow}>
                  <Label>Interval</Label>
                  <Select
                    value={{ value: intervalPicker, label: intervalPicker }}
                    options={intervals.map(interval => ({ value: interval, label: interval }))}
                    onChange={(selectedOption) => setIntervalPicker(selectedOption.value)}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: 'var(--input-bg)',
                        color: 'var(--body-color)',
                        borderColor: 'var(--input-border)',
                        width: '200px'
                      }),
                      singleValue: (base) => ({ ...base, color: 'var(--body-color)' }),
                      menu: (base) => ({ ...base, backgroundColor: 'var(--card-bg)', color: 'var(--body-color)' }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? 'var(--sidebar-hover-bg)' : 'transparent',
                        color: 'var(--body-color)'
                      })
                    }}
                  />
                  <Label>Type</Label>
                  <Select
                    value={{ value: typePicker, label: typePicker }}
                    options={types.map(type => ({ value: type, label: type }))}
                    onChange={(selectedOption) => setTypePicker(selectedOption.value)}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: 'var(--input-bg)',
                        color: 'var(--body-color)',
                        borderColor: 'var(--input-border)',
                        width: '200px'
                      }),
                      singleValue: (base) => ({ ...base, color: 'var(--body-color)' }),
                      menu: (base) => ({ ...base, backgroundColor: 'var(--card-bg)', color: 'var(--body-color)' }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? 'var(--sidebar-hover-bg)' : 'transparent',
                        color: 'var(--body-color)'
                      })
                    }}
                  />
                </div>
              </div>
            )}

            {(() => {
              // Show loading when date range is changing
              if (isDateRangeChanging) {
                return <div className={styles.LoadingMessage}>Loading data...</div>;
              }
              
              switch (dataProcessingStatus) {
                case "loading":
                  return <div className={styles.LoadingMessage}>Loading data...</div>;
                case "no data":
                  return <div className={styles.NoDataMessage}>No data found</div>;
                case "loaded data":
                  return (
                    <div>
                      {lineChartData.length > 0 && (
                        <div className={styles.LineChartContainer}>
                          <h6 className="MuiTypography-h6">Active Power</h6>
                          <CustomChart
                            lineChartData={lineChartData}
                            unit={"kW"}
                          />
                        </div>
                      )}
                      {!isLiveData && 
                        dayCapData.length > 0 && (
                        <div style={{ marginTop: '80px' }}>
                          <h6 className="MuiTypography-h6">Daily Yield</h6>
                          <DayCapStackedBarChart
                            dayCapData={dayCapData}
                            unit={"kWh"}
                          />
                        </div>
                      )}
                    </div>
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </>
      )}
    </div>
  );
}

export default SolarFacilities;
